// Javelin budget-alert admin sweep — POST /api/_admin/javelin/budget_alert_sweep.
// Shape snapped from the 2026-06-11 prod hotfix (merged into v2): the
// endpoint checks every active moderator's canonical budget
// (modBudgetBalanceCents) and notifies the budget-alert recipients with
// one summary of mods under $500. dry_run computes without sending.
import { z } from 'zod';

export const JavBudgetSweepReqSchema = z.object({
  dry_run: z.boolean().optional(),
});
export type JavBudgetSweepReq = z.infer<typeof JavBudgetSweepReqSchema>;

export const JavBudgetSweepLowModSchema = z.object({
  mod_id: z.string(),
  name: z.string(),
  balance_usd: z.number(),
});
export type JavBudgetSweepLowMod = z.infer<typeof JavBudgetSweepLowModSchema>;

export const JavBudgetSweepResSchema = z.object({
  ok: z.literal(true),
  dry_run: z.boolean(),
  threshold_usd: z.number(),
  recipients: z.array(z.string()),
  low_mods: z.array(JavBudgetSweepLowModSchema),
  message: z.string(),
  sends: z.array(z.object({
    chat: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
  })),
});
export type JavBudgetSweepRes = z.infer<typeof JavBudgetSweepResSchema>;

// GET /api/javelin/budgetLedger?mod_id=NN — детализация движения бюджета
// одного мода (delegation.ts#budgetLedger). Витрина за итогами каскада:
// кто, когда и сколько выдал, и кому мод раздал сам.
export const JavBudgetLedgerQuerySchema = z.object({
  /** Либо mod_id человека (его операционный бюджет), либо 'ADMIN' — общая
   *  касса. Фильтра «какой карман» больше нет: с migration 0035 это разные
   *  счета с разными mod_id, и смешать их в одном списке невозможно. */
  mod_id: z.string().trim().min(1),
});
export type JavBudgetLedgerQuery = z.infer<typeof JavBudgetLedgerQuerySchema>;

export const JavBudgetLedgerRowSchema = z.object({
  id: z.string(),
  ts: z.string(),
  kind: z.string(),
  direction: z.string(),
  amount_usd: z.number(),
  note: z.string(),
  counterparty_mod_id: z.string(),
  /** Имя второй стороны; 'PM' для прямого пополнения, '—' если её нет. */
  counterparty_name: z.string(),
  status: z.string(),
  iteration: z.string(),
  wallet: z.string(),
  /** Кто из админов провёл операцию (только у строк кассы). */
  acting_name: z.string().default(''),
});
export type JavBudgetLedgerRow = z.infer<typeof JavBudgetLedgerRowSchema>;

export const JavBudgetLedgerResSchema = z.object({
  mod_id: z.string(),
  mod_name: z.string(),
  rows: z.array(JavBudgetLedgerRowSchema),
  truncated: z.boolean(),
  totals: z.object({
    in_from_pm: z.number(),
    in_from_admin: z.number(),
    out_delegated: z.number(),
  }),
  /** Расклад остатка счёта, который открыт: у кассы — пул, у человека —
   *  стоимость его съёмок. Общего числа между счетами нет. */
  breakdown: z.object({
    spent_own_sessions: z.number(),
    balance: z.number(),
  }),
});
export type JavBudgetLedgerRes = z.infer<typeof JavBudgetLedgerResSchema>;

// ─── Ставка списания за сессию (админ управляет сам, 2026-07-28) ──────────
// Стоимость съёмки фиксируется НА участнике в момент захвата (migration
// 0036), поэтому смена ставки не переоценивает уже снятые сессии — сколько
// бы раз её ни меняли. Worker: delegation.ts#adminModRates / adminSetModRate.

export const JavAdminModRatesResSchema = z.looseObject({
  /** Потолок ставки в долларах — сервер режет попытки выше. */
  ceiling_usd: z.number(),
  rows: z.array(z.looseObject({
    mod_id: z.string(),
    name: z.string(),
    rate_usd: z.number(),
    /** С какой даты действует; '' — персональной ставки нет, общий тариф. */
    since: z.string(),
    /** Сколько раз ставку меняли (вся история хранится). */
    changes: z.number(),
  })),
});
export type JavAdminModRatesRes = z.infer<typeof JavAdminModRatesResSchema>;

export const JavAdminSetModRateReqSchema = z.object({
  mod_id: z.string().trim().min(1),
  rate_usd: z.number().positive(),
});
export type JavAdminSetModRateReq = z.infer<typeof JavAdminSetModRateReqSchema>;

export const JavAdminSetModRateResSchema = z.looseObject({
  ok: z.boolean(),
  rate_usd: z.number(),
  /** Не было изменений — прислали ту же ставку. */
  unchanged: z.boolean().optional(),
  since: z.string().optional(),
  previous_usd: z.number().optional(),
});
export type JavAdminSetModRateRes = z.infer<typeof JavAdminSetModRateResSchema>;

// GET /api/javelin/modSessionCharges?mod_id= — расходы бюджета модератора:
// каждая съёмка со своей зафиксированной ценой (migration 0036). Пара к
// budgetLedger, который показывает приходы. Worker: delegation.ts.
export const JavModSessionChargesQuerySchema = z.object({
  mod_id: z.string().trim().min(1),
});
export type JavModSessionChargesQuery = z.infer<typeof JavModSessionChargesQuerySchema>;

export const JavModSessionChargesResSchema = z.looseObject({
  mod_id: z.string(),
  mod_name: z.string(),
  count: z.number(),
  total_usd: z.number(),
  /** Ставка, по которой спишется СЛЕДУЮЩАЯ съёмка. */
  current_rate_usd: z.number(),
  truncated: z.boolean().optional(),
  rows: z.array(z.looseObject({
    pid: z.string(),
    session_id: z.string(),
    date: z.string(),
    cost_usd: z.number(),
    tracker_status: z.string(),
  })),
});
export type JavModSessionChargesRes = z.infer<typeof JavModSessionChargesResSchema>;
